// src/helpers/dataSellsService.js
import { get, post, put, del } from './apiHelper';

/**
 * Servicio para gestión de ventas desde archivos CSV/Excel
 */

// ==================== PASO 1: SUBIR ARCHIVO ====================

/**
 * Sube un archivo CSV o Excel al CDN
 * @param {File} file - Archivo a subir
 * @param {string} marcaId - ID de la marca
 * @param {string[]} tiendasIds - Array de IDs de tiendas
 * @returns {Promise<Object>} Datos del archivo subido
 */
export const uploadDataFile = async (file, marcaId, tiendasIds) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('marca', marcaId);

    // Añadir tiendas al FormData
    tiendasIds.forEach(tiendaId => {
        formData.append('tiendas[]', tiendaId);
    });

    const response = await post('data/upload', formData);
    return response.data;
};

// ==================== PASO 2: VALIDAR Y PROCESAR ====================

/**
 * Valida un archivo subido (extrae header y 2 líneas de datos)
 * @param {string} archivoId - ID del archivo subido
 * @returns {Promise<Object>} Resultado de la validación
 */
export const validarArchivo = async (archivoId) => {
    return await post(`data-sells/validar/${archivoId}`);
};

/**
 * Mapea campos automáticamente usando IA
 * @param {string} archivoId - ID del archivo subido
 * @returns {Promise<Object>} Resultado del mapeo automático
 */
export const mapearCamposConIA = async (archivoId) => {
    return await post(`data-sells/mapear-campos/${archivoId}`);
};

/**
 * Obtiene los campos disponibles en un archivo
 * @param {string} archivoId - ID del archivo subido
 * @returns {Promise<Object>} Headers y datos preview
 */
export const obtenerCamposDisponibles = async (archivoId) => {
    return await get(`data-sells/campos-disponibles/${archivoId}`);
};

/**
 * Crea un mapeo manual de campos
 * @param {string} archivoId - ID del archivo
 * @param {string} sistemaOrigen - Sistema origen (shopify, square, etc.)
 * @param {Object} mapeoCampos - Objeto con el mapeo de campos
 * @param {Object} configuracionFormato - Configuración de formato
 * @returns {Promise<Object>} Mapeo creado
 */
export const crearMapeoManual = async (
    archivoId,
    sistemaOrigen,
    mapeoCampos,
    configuracionFormato = {}
) => {
    return await post('data-sells/mapeo-manual', {
        archivoId,
        sistemaOrigen,
        mapeoCampos,
        configuracionFormato
    });
};

/**
 * Actualiza un mapeo manual existente
 * @param {string} archivoId - ID del archivo
 * @param {Object} mapeoCampos - Campos a actualizar
 * @param {Object} configuracionFormato - Configuración de formato
 * @returns {Promise<Object>} Mapeo actualizado
 */
export const actualizarMapeoManual = async (
    archivoId,
    mapeoCampos,
    configuracionFormato = {}
) => {
    return await put(`data-sells/mapeo-manual/${archivoId}`, {
        mapeoCampos,
        configuracionFormato
    });
};

/**
 * Procesa y guarda todas las ventas del archivo
 * @param {string} archivoId - ID del archivo subido
 * @returns {Promise<Object>} Estadísticas del procesamiento
 */
export const procesarVentas = async (archivoId) => {
    return await post(`data-sells/procesar/${archivoId}`);
};

// ==================== CONSULTAS DE VENTAS ====================

/**
 * Obtiene todas las ventas
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de ventas
 */
export const obtenerVentas = async (filters = {}) => {
    try {
        // Construir query string con filtros
        const queryParams = new URLSearchParams();

        if (filters.marcaId) queryParams.append('marcaId', filters.marcaId);
        if (filters.tiendaId) queryParams.append('tiendaId', filters.tiendaId);
        if (filters.fechaDesde) queryParams.append('fechaDesde', filters.fechaDesde);
        if (filters.fechaHasta) queryParams.append('fechaHasta', filters.fechaHasta);
        if (filters.sistemaOrigen) queryParams.append('sistemaOrigen', filters.sistemaOrigen);
        if (filters.estadoFinanciero) queryParams.append('estadoFinanciero', filters.estadoFinanciero);

        const endpoint = queryParams.toString()
            ? `data-sells?${queryParams.toString()}`
            : 'data-sells';

        const response = await get(endpoint);
        const ventas = response.data || response || [];
        return Array.isArray(ventas) ? ventas : [];
    } catch (error) {
        console.error('Error obteniendo ventas:', error);
        // Si el endpoint no existe o hay error, retornar array vacío
        return [];
    }
};

/**
 * Obtiene una venta por ID
 * @param {string} ventaId - ID de la venta
 * @returns {Promise<Object>} Datos de la venta
 */
export const obtenerVentaPorId = async (ventaId) => {
    const response = await get(`data-sells/${ventaId}`);
    return response.data || response;
};

/**
 * Obtiene el número de ventas procesadas de un archivo
 * @param {string} archivoId - ID del archivo
 * @returns {Promise<number>} Número de ventas
 */
export const contarVentasPorArchivo = async (archivoId) => {
    try {
        const response = await get(`data-sells?archivoOrigen=${archivoId}`);
        const ventas = response.data || response;
        return Array.isArray(ventas) ? ventas.length : 0;
    } catch (error) {
        return 0;
    }
};

/**
 * Obtiene todos los archivos subidos con filtros opcionales
 * @param {Object} filters - Filtros opcionales
 * @param {string} filters.marca - ID de la marca
 * @param {string} filters.fechaDesde - Fecha de inicio
 * @param {string} filters.fechaHasta - Fecha de fin
 * @param {string} filters.tipoArchivo - Tipo de archivo (csv o excel)
 * @returns {Promise<Array>} Lista de archivos subidos
 */
export const obtenerArchivosSubidos = async (filters = {}) => {
    try {
        // Construir query string con filtros
        const queryParams = new URLSearchParams();

        if (filters.marca) queryParams.append('marca', filters.marca);
        if (filters.fechaDesde) queryParams.append('fechaDesde', filters.fechaDesde);
        if (filters.fechaHasta) queryParams.append('fechaHasta', filters.fechaHasta);
        if (filters.tipoArchivo) queryParams.append('tipoArchivo', filters.tipoArchivo);

        const endpoint = queryParams.toString()
            ? `data/uploads?${queryParams.toString()}`
            : 'data/uploads';

        const response = await get(endpoint);
        const archivos = response.data || response || [];
        return Array.isArray(archivos) ? archivos : [];
    } catch (error) {
        console.error('Error obteniendo archivos subidos:', error);
        return [];
    }
};

/**
 * Obtiene archivos subidos por marca
 * @param {string} marcaId - ID de la marca
 * @returns {Promise<Array>} Lista de archivos de la marca
 */
export const obtenerArchivosPorMarca = async (marcaId) => {
    return obtenerArchivosSubidos({ marca: marcaId });
};

/**
 * Obtiene un archivo por ID
 * @param {string} archivoId - ID del archivo
 * @returns {Promise<Object>} Datos del archivo
 */
export const obtenerArchivoPorId = async (archivoId) => {
    try {
        const response = await get(`data/uploads/${archivoId}`);
        return response.data || response;
    } catch (error) {
        console.error('Error obteniendo archivo por ID:', error);
        throw error;
    }
};

/**
 * Elimina un archivo del CDN y de la base de datos
 * @param {string} archivoId - ID del archivo a eliminar
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const eliminarArchivo = async (archivoId) => {
    try {
        const response = await del(`data/uploads/${archivoId}`);
        return response;
    } catch (error) {
        console.error('Error eliminando archivo:', error);
        throw error;
    }
};

// ==================== ENRIQUECIMIENTO ====================

/**
 * Enriquece tickets con información de ventas
 * @param {string} marcaId - ID de la marca
 * @param {string} fechaDesde - Fecha de inicio (opcional)
 * @param {string} fechaHasta - Fecha de fin (opcional)
 * @returns {Promise<Object>} Resultado del enriquecimiento
 */
export const enriquecerTickets = async (marcaId, fechaDesde = null, fechaHasta = null) => {
    const body = {};
    if (fechaDesde) body.fechaDesde = fechaDesde;
    if (fechaHasta) body.fechaHasta = fechaHasta;

    return await post(`data-sells/enriquecer/${marcaId}`, body);
};

// ==================== CAMPOS ESTÁNDAR DEL SISTEMA ====================

/**
 * Lista de campos estándar del sistema para mapeo
 */
export const CAMPOS_ESTANDAR = {
    // Identificación
    idVenta: 'ID único de la venta',
    fecha: 'Fecha de la venta',
    hora: 'Hora de la venta',

    // Información financiera
    monto: 'Monto total de la venta',
    subtotal: 'Subtotal antes de impuestos',
    impuestos: 'Total de impuestos (IVA, IGV, etc.)',
    porcentajeImpuestos: 'Porcentaje de impuestos',
    envio: 'Costo de envío',
    moneda: 'Moneda (EUR, USD, PEN, CLP, etc.)',
    montoConDescuento: 'Monto con descuento aplicado',

    // Información de productos
    nombreProducto: 'Nombre del producto',
    codigoProducto: 'SKU o código del producto',
    cantidad: 'Cantidad vendida',
    precio: 'Precio unitario',
    precioSinImpuestos: 'Precio sin impuestos',
    precioConImpuestos: 'Precio con impuestos',
    totalLinea: 'Total de la línea',
    categoria: 'Categoría del producto',

    // Información de pago
    tipoPago: 'Tipo de pago (Tarjeta, Efectivo, etc.)',
    metodoPago: 'Método de pago específico',
    comision: 'Comisión aplicada',
    neto: 'Monto neto después de comisiones',

    // Información del cliente
    nombreCliente: 'Nombre del cliente',
    email: 'Email del cliente',
    telefono: 'Teléfono del cliente',
    nombreFacturacion: 'Nombre de facturación',
    nombreEnvio: 'Nombre de envío',

    // Estados
    estadoFinanciero: 'Estado financiero (paid, pending, refunded, etc.)',
    estadoCumplimiento: 'Estado de cumplimiento (fulfilled, unfulfilled, etc.)'
};

/**
 * Sistemas origen soportados
 */
export const SISTEMAS_ORIGEN = [
    { value: 'shopify', label: 'Shopify' },
    { value: 'square', label: 'Square' },
    { value: 'sumup', label: 'SumUp' },
    { value: 'catinfog', label: 'Catinfog' },
    { value: 'woocommerce', label: 'WooCommerce' },
    { value: 'prestashop', label: 'PrestaShop' },
    { value: 'magento', label: 'Magento' },
    { value: 'otro', label: 'Otro' }
];

/**
 * Formatos de fecha soportados
 */
export const FORMATOS_FECHA = [
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-01-15)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/01/2024)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/15/2024)' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (15-01-2024)' },
    { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD (2024/01/15)' }
];

/**
 * Separadores decimales
 */
export const SEPARADORES_DECIMAL = [
    { value: '.', label: 'Punto (.)' },
    { value: ',', label: 'Coma (,)' }
];

